/* eslint-disable react/react-in-jsx-scope */
import $ from 'jquery';
import { isEmpty, extend } from 'underscore';
import { endsWith } from 'underscore.string';
import Promise from 'bluebird';
import React from 'react';
import kendo from '@bingads-webui/kendo/kendo.core';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { languageParseMode } from './countdown-language';

import { CountdownEditor } from './editor/editor-view';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { parseRule, evaluateCountdownRules, parseCountdownRule } from './parser';
import { formatCountdown, defaultCountdownDays } from './utils';

const countDownDefaultDaysString = kendo.toString(kendo.date.addDays(new Date(), defaultCountdownDays), 'yyyy/MM/dd', 'en-Us');
const RSACountDownDefaultDaysString = kendo.toString(kendo.date.addDays(new Date(), defaultCountdownDays), 'yyyy-MM-dd', 'en-Us');

const pickerCountdownInsert = `{=countdown("${countDownDefaultDaysString}")}`;
const RSAPickerCountdownInsert = `{countdown(${RSACountDownDefaultDaysString})}`;

const isDatePicker = (element) => {
  const $target = $(element);
  return $target.closest('#countdowndatepicker_dateview').length > 0 ||
    $target.closest('.k-calendar-container').length > 0 ||
    $target.closest('.k-content').length > 0;
};

export class AdCustomizerCountdownAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      config,
      instrumentation,
      i18n,
      countdownI18nDummy,
    },
    controller,
    excludedFields = [],
  }) {
    super({
      name: 'countdown',
      config,
      priority: 10,
      instrumentation,
      i18n,
      activityName: 'countdown',
      controller,
      excludedFields,
    });

    this.i18nDummy = countdownI18nDummy;
  }

  getMenuItem({
    startIndex, source, target: { value }, updateAndCloseCallback, adType,
  }) {
    return {
      name: this.i18n.getString('Picker_Countdown_Label'),
      description: this.i18n.getString('Picker_Countdown_Description'),
      onClick: (event) => {
        const endIndex = startIndex + source.length;

        const prefix = value.substring(0, startIndex);
        const suffix = value.substring(endIndex);
        let newValue;
        if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
          newValue = `${prefix}${RSAPickerCountdownInsert}${suffix}`;
        } else {
          newValue = `${prefix}${pickerCountdownInsert}${suffix}`;
        }

        const cursorPosition = newValue.length - suffix.length - 1;

        updateAndCloseCallback({
          newValue,
          cursorPosition,
          event,
          isSelectedByUser: true,
        });
      },
    };
  }

  isClickedAway(event) {
    return !isDatePicker(event.target);
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target, adType, field,
  }) {
    const processedText = text ? text.toLowerCase() : '';
    const rule = parseRule({
      text: processedText,
      cursorIndex,
      i18n: this.i18n,
      config: this.config,
      adType,
    });

    if (rule) {
      const { options: { includeAdCustomizerFeed, startIndex, source } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: (includeAdCustomizerFeed || this.excludedFields.indexOf(field) !== -1) ?
            [] : [this.getMenuItem({
              startIndex,
              source,
              updateAndCloseCallback,
              target,
              adType,
            })],
        },
        rule
      ));
    }

    // not match whole rule, see whether to show menu item
    let startIndex1 = -1;
    let source;
    if (endsWith(processedText, '{=', cursorIndex) && adType !== 'ResponsiveSearchAd' && adType !== 'ResponsiveAd') {
      startIndex1 = cursorIndex - 2;
      source = '{=';
    } else if (endsWith(processedText, '{', cursorIndex)) {
      startIndex1 = cursorIndex - 1;
      source = '{';
    }

    if (startIndex1 >= 0 && startIndex1 !== cursorIndex) {
      return Promise.resolve(extend({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: this.excludedFields.indexOf(field) !== -1 ? [] : [this.getMenuItem({
          startIndex: startIndex1,
          source,
          updateAndCloseCallback,
          target,
          adType,
        })],
      }));
    }

    return Promise.resolve(null);
  }

  getSyntax(analyzeResult, adType) {
    const { options } = analyzeResult || {};
    const { type } = options || {};
    if (isEmpty(type)) {
      return null;
    }

    const syntax = formatCountdown(
      `${this.i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} `,
      type,
      null,
      null,
      this.i18n.getString('CountdownEditor_Syntax_Language_Label'),
      this.i18n.getString('CountdownEditor_Syntax_DaysBefore_Label'),
      null,
      adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd'
    );
    return (
      <div className="countdownEditor-syntax">{syntax}
        <HelpIcon
          topic="pop_BA_NewAdCreate_Countdown_Syntax"
          ariaLabel={this.i18n.getString('Click to learn more about {{item}}', { item: this.i18n.getString('AdCustomizer_Syntax_Syntax') })}
        />
      </div>
    );
  }

  // as long as the rule match (means has type), we can show the editor
  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    const { type, includeAdCustomizerFeed } = options || {};
    return !includeAdCustomizerFeed && !isEmpty(type);
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback, adType,
  }) {
    const { options } = analyzeResult || {};
    const editor = new CountdownEditor({
      options,
      updateAndCloseCallback,
      i18n: this.i18n,
      target,
      config: this.config,
      adType,
    }).render();

    return (<BackboneInstanceWrapper
      managed
      instance={editor}
    />);
  }

  evaluateRule(text, adType = 'ExpandedTextAd') {
    return evaluateCountdownRules({
      text, i18n: this.i18n, i18nDummy: this.i18nDummy, config: this.config, adType,
    });
  }

  getCharCountExcludingAdCustomizers(text, isComplete, adType = 'ExpandedTextAd') {
    let processedText = text;
    let charCount = 0;
    const rule = parseCountdownRule({
      text: processedText,
      startIndex: 0,
      languageParseMode: languageParseMode.ForCharacterCount,
      config: this.config,
      i18n: this.i18n,
      adType,
    });

    if (rule && rule.source && (!isComplete || rule.isComplete)) {
      if (rule.isComplete) {
        charCount += rule.language.maxLength;
      } else {
        charCount += rule.source.length;
      }
      processedText = processedText.replace(rule.source, '');
    }

    return {
      processedText,
      charCount,
    };
  }
}
